import { ReactNode } from 'react'
import useProductEnabled from '../../hooks/useProductEnabled'

interface FeatureGateProps {
  /**
   * The product's menu id. Prefer `MENU_ID.SOME_PRODUCT` from
   * src/environment/featureFlags over a string literal - flag resolution is
   * default-open, so a typo renders the children and the gate silently does
   * nothing.
   */
  flag: string
  /**
   * Also require the product be offered in the user's region. Off by default:
   * the region product sets are keyed by side-nav ids, so a product with no nav
   * item is in no region set and would be blocked everywhere. Pass it for
   * nav-keyed products where region coverage is meaningful.
   */
  requireRegion?: boolean
  /** Rendered when the gate is closed. Defaults to nothing. */
  fallback?: ReactNode
  children: ReactNode
}

/**
 * Renders children only when the product is on in this build.
 *
 *   <FeatureGate flag={MENU_ID.ALERTING_MONITORS} requireRegion>
 *     <Link to="/alerts">...</Link>
 *   </FeatureGate>
 */
export default function FeatureGate({
  flag,
  requireRegion = false,
  fallback = null,
  children,
}: FeatureGateProps) {
  const gate = useProductEnabled()
  const open = requireRegion ? gate.isAvailable(flag) : gate.isEnabled(flag)
  return <>{open ? children : fallback}</>
}
